import {
	RiArrowDropDownLine,
	RiArrowDropUpLine,
	RiFlashlightFill,
} from '@remixicon/react';
import { useState } from 'react';
import { usePicksContext } from '../../contexts/Picks/PicksContext';
import { order, transformInitial } from '../../helpers/utils';
import { FeedPickProps } from '../../types/pickFeed';
import { FilterScouts } from '../Filters/FilterScouts';
import { FilterTournamentGroup } from '../Filters/FilterTournamentGroup';
import { StatisticsPick } from '../Widgets/Statistics/indexPick';

interface IProps {
	item: FeedPickProps[];
	isLoading: boolean;
}

const FeedPickItem = ({ item, isLoading }: IProps) => {
	const { picks, SetPicks } = usePicksContext();
	const [selectedScouts, setSelectedScouts] = useState<string[]>(['Todos']);
	const [playerStatsOpen, setPlayerStatsOpen] = useState(false);
	const [selectGroupTournament, setSelectGroupTournament] = useState<any>(
		item[0].id
	);

	const verifyPickEntry = (pick: any): boolean => {
		const pickEntry = picks.find(
			(pickEntry: any) =>
				pickEntry.tournamentGroupId === pick.tournamentGroupId &&
				pickEntry.pick.playerId === pick.pick.playerId &&
				pickEntry.pick.scoutId === pick.pick.scoutId &&
				pickEntry.pick.teamId === pick.pick.teamId &&
				pickEntry.pick.type === pick.pick.type &&
				pickEntry.pickSelection === pick.pickSelection
		);
		return !!pickEntry;
	};

	const scoutAvailable = (): string[] => {
		const tournamentGroup = item.find(
			(i) => i.id === selectGroupTournament
		);
		if (!tournamentGroup || !tournamentGroup.picksAvailable) {
			return ['Todos'];
		}
		const allScoutNames = tournamentGroup.picksAvailable.map(
			(pick) => pick.scout.name
		);
		const uniqueScoutNames = Array.from(new Set(allScoutNames));

		const sortedScoutNames = uniqueScoutNames.sort((a, b) => {
			const indexA = order.indexOf(a);
			const indexB = order.indexOf(b);

			const safeIndexA = indexA === -1 ? 999 : indexA;
			const safeIndexB = indexB === -1 ? 999 : indexB;

			return safeIndexA - safeIndexB;
		});

		return ['Todos', ...sortedScoutNames];
	};
	const scoutsAvailable = scoutAvailable();

	const sortedPlayers = item
		.filter((i) => i.id === selectGroupTournament)
		.flatMap((tournamentGroup) =>
			tournamentGroup.picksAvailable.flatMap((picks) =>
				picks.pickSelection.map((playerAvailable) => ({
					...playerAvailable,
					player: picks.player,
					team: picks.team,
					scout: picks.scout,
					eventId: picks.eventId,
					tournamentGroupId: tournamentGroup.id,
					tournamentPickId: picks.id,
				}))
			)
		);

	return (
		<div>
			<FilterTournamentGroup
				data={item}
				loading={isLoading}
				filters={selectGroupTournament}
				setFilters={setSelectGroupTournament}
			/>
			<FilterScouts
				data={scoutsAvailable}
				loading={isLoading}
				filters={selectedScouts}
				setFilters={setSelectedScouts}
			/>

			<div className="flex px-3 flex-col gap-3">
				<div className="grid max-[375px]:grid-cols-2 grid-cols-3 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-8 gap-3 overflow-auto px-2 flex-1">
					{sortedPlayers
						.filter(
							(p) =>
								selectedScouts.length === 0 ||
								selectedScouts.includes('Todos') ||
								selectedScouts.includes(p.scout.name)
						)
						.sort((a, b) => {
							if (b.pick !== a.pick) {
								return b.pick - a.pick;
							}
							const scoutCompare = a.scout.name.localeCompare(
								b.scout.name
							);
							if (scoutCompare !== 0) {
								return scoutCompare;
							}
							return a.player.name.localeCompare(b.player.name);
						})

						.map((player, index) => {
							const selectPick = {
								tournamentGroupId: player.tournamentGroupId,
								tournamentPickId: player.tournamentPickId,
								pick: player,
								event: player.eventId,
								player: player.player,
								team: player.team,
								scout: player.scout,
							};
							const pickSelectUp = {
								...selectPick,
								pickSelection: 'up',
							};
							const pickSelectDown = {
								...selectPick,
								pickSelection: 'down',
							};
							return (
								<div
									key={`${player.tournamentPickId}-${player.player.id}-${player.type}`}
									className="cursor-pointer bg-surface-action-secondary rounded-[5px]"
								>
									<div className="relative">
										<div className="w-full px-2 py-1.5 justify-between items-center inline-flex flex-col gap-1">
											{/* {player.type === 'promo' && (
												<RiStarFill className="w-4 absolute top-2.5 left-2.5 text-custom-yellow" />
											)} */}
											<StatisticsPick
												scoutId={player.scoutId}
												playerId={player.player.id}
												playerStatsOpen={
													playerStatsOpen
												}
												setPlayerStatsOpen={
													setPlayerStatsOpen
												}
											/>

											<div className="flex flex-row items-center justify-center relative">
												{player.team.image ? (
													<img
														className="w-8 h-8 object-cover -mr-2"
														src={player.team.image}
													/>
												) : (
													<div className="w-8 h-8 rounded-full -mr-2 text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
														{transformInitial(
															player.team.name
														)}
													</div>
												)}
												{player.player.image ? (
													<img
														className="w-8 h-8 rounded-[33.55px] object-cover"
														src={
															player.player.image
														}
													/>
												) : (
													<div className="w-8 h-8 rounded-full text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
														{transformInitial(
															player.player.name
														)}
													</div>
												)}
												<div className="text-custom-gray4 absolute bottom-0 left-[57px] text-[10px] text-center font-normal font-bahnschrift">
													{player.player.position}
												</div>
											</div>
											<div className="flex items-center justify-center text-center">
												<div className="text-text/heading-default max-[420px]:text-xs text-sm text-center font-normal font-bahnschrift leading-[16px]">
													{player.player.name}
												</div>
											</div>
											<div className="flex flex-col">
												<span className="text-text/heading-default min-h-6 text-lg flex justify-center text-center font-normal font-bahnschrift ">
													{player.pick}
													<span className="text-custom-gray4 absolute right-4 text-[10px] text-center font-normal font-bahnschrift">
														*Beta*
													</span>
												</span>
												<span className="text-text/heading-default -mt-1 text-sm text-center font-normal font-bahnschrift">
													{player.pick > 0
														? player.scout.name
														: player.scout.name}
												</span>
											</div>

											<div className="flex flex-row gap-0.5 items-center justify-between w-full">
												{player.up && (
													<div
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															SetPicks(
																pickSelectUp
															);
														}}
														className={
															'w-full flex-1 flex-col border border-custom-border items-center justify-center' +
															(player.down
																? ' rounded-s-md px-1.5 w-1/2 max-xl:-ml-1.5 max-md:-ml-0.5 max-[440px]:-ml-0'
																: ' rounded-md w-full') +
															(Number(
																player.factor
															) !== 1
																? ' '
																: ' py-0.5') +
															(verifyPickEntry(
																pickSelectUp
															)
																? ' bg-primary'
																: '')
														}
													>
														<div
															className={
																'flex flex-row items-center justify-center text-[10px] font-normal font-bahnschrift' +
																(verifyPickEntry(
																	pickSelectUp
																)
																	? ' text-white'
																	: ' text-text-paragraph')
															}
														>
															<RiArrowDropUpLine
																className={
																	'-mr-1 max-md:-ml-1 max-[460px]:-ml-1' +
																	(Number(
																		player.factor
																	) !== 1
																		? ' -mt-3'
																		: ' ')
																}
															/>
															<span className="mr-2 max-[415px]:mr-0.5 flex flex-col items-center">
																Mais
																{Number(
																	player.factor
																) !== 1 && (
																	<span
																		className={
																			'-mt-[10px] relative flex flex-row text-center justify-center items-center text-[10px] font-normal font-bahnschrift' +
																			(verifyPickEntry(
																				pickSelectUp
																			)
																				? ' text-white'
																				: ' text-text-paragraph')
																		}
																	>
																		<RiFlashlightFill className="w-3.5 text-custom-yellow" />
																		{
																			player.factor
																		}
																		x
																	</span>
																)}
															</span>
														</div>
													</div>
												)}
												{player.down && (
													<div
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															SetPicks(
																pickSelectDown
															);
														}}
														className={
															'flex flex-col border border-custom-border items-center justify-center' +
															(player.up
																? ' rounded-e-md px-1.5 w-1/2 max-md:-ml-1 '
																: ' rounded-md w-full') +
															(Number(
																player.factor
															) !== 1
																? ' '
																: ' py-0.5') +
															(verifyPickEntry(
																pickSelectDown
															)
																? ' bg-primary'
																: '')
														}
													>
														<div
															className={
																'flex items-center justify-center text-text-paragraph text-[10px] font-normal font-bahnschrift' +
																(verifyPickEntry(
																	pickSelectDown
																)
																	? ' text-white'
																	: ' text-text-paragraph')
															}
														>
															<span className="ml-2">
																Menos
																{Number(
																	player.factor
																) !== 1 && (
																	<span
																		className={
																			'-mt-[10px] flex flex-row text-center justify-center items-center text-[10px] font-normal font-bahnschrift' +
																			(verifyPickEntry(
																				pickSelectDown
																			)
																				? ' text-white'
																				: ' text-text-paragraph')
																		}
																	>
																		<RiFlashlightFill className="w-3.5 text-custom-yellow" />
																		{
																			player.factor
																		}
																		x
																	</span>
																)}
															</span>
															<RiArrowDropDownLine
																className={
																	'w-6 relative -ml-1' +
																	(Number(
																		player.factor
																	) !== 1
																		? ' -mt-3'
																		: ' ')
																}
															/>
														</div>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>
							);
						})}
				</div>
			</div>
		</div>
	);
};
export default FeedPickItem;
